import { getLatestValueFromHistory } from "../api/api";

const todayISO = () => new Date().toISOString().split("T")[0];

export const normalizeEffectiveDate = (date) => {
  if (!date) return "";
  return String(date).split("T")[0].substring(0, 10);
};

export const effectiveDateOrToday = (date) =>
  normalizeEffectiveDate(date) || todayISO();

/** null/undefined — не уволен */
export const normalizeIsFired = (isFired) => {
  if (isFired === undefined || isFired === null) return false;
  return isFired === true || isFired === "true" || isFired === 1;
};

/** ID отделений (department) из позиций или данных пользователя */
export const resolveDepartmentIds = (positions, userData) => {
  const fromPositions = (positions || [])
    .filter((p) => p.department?.value != null)
    .map((p) => Number(p.department.value));
  if (fromPositions.length > 0) {
    return [...new Set(fromPositions)];
  }
  if (userData?.department?.length) {
    return userData.department.map((d) => Number(d.id));
  }
  if (userData?.hr_data?.primary_department?.id != null) {
    return [Number(userData.hr_data.primary_department.id)];
  }
  if (userData?.departments?.length) {
    return userData.departments.map((d) => Number(d.id));
  }
  return [];
};

const resolveCompanyId = (formData, positions, userData) => {
  const fromPrimaryCard = positions?.[0]?.company?.value;
  if (fromPrimaryCard != null && fromPrimaryCard !== "") {
    return Number(fromPrimaryCard);
  }
  if (formData?.companyId != null && formData.companyId !== "") {
    return Number(formData.companyId);
  }
  if (userData?.company_id != null) return Number(userData.company_id);
  return null;
};

const nestedHistoryId = (raw) =>
  raw?.company_id ??
  raw?.group_id ??
  raw?.department_id ??
  raw?.section_id ??
  raw?.role_id ??
  raw?.team_leader_id ??
  raw?.id;

/** Последняя дата истории для конкретного id, без подстановки «сегодня». */
const latestHistoryDateForId = (history, fieldName, valueId) => {
  if (!Array.isArray(history) || valueId == null) return "";
  const match = [...history]
    .filter((item) => item?.effective_date != null)
    .sort((a, b) => {
      const byDate = new Date(b.effective_date) - new Date(a.effective_date);
      if (byDate !== 0) return byDate;
      return (b.id ?? 0) - (a.id ?? 0);
    })
    .find((item) => {
      const raw = item[fieldName] ?? item;
      if (raw == null || typeof raw !== "object") return false;
      return Number(nestedHistoryId(raw)) === Number(valueId);
    });
  return normalizeEffectiveDate(match?.effective_date);
};

/**
 * Список {id, effective_date} по карточкам.
 * Одинаковый id с разными датами сохраняется дважды.
 * Пустую дату не заменяем на сегодня.
 */
const datedOrgItemsFromPositions = (positions, getId, getDate) => {
  const items = [];
  const seen = new Set();
  (positions || []).forEach((position) => {
    const rawId = getId(position);
    if (rawId == null || rawId === "") return;
    const effectiveDate = normalizeEffectiveDate(getDate(position));
    const item = { id: Number(rawId) };
    if (effectiveDate) item.effective_date = effectiveDate;
    const key = `${item.id}:${item.effective_date || ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push(item);
  });
  return items;
};

const withHistoryDates = (items, history, fieldName) =>
  items.map((item) => {
    if (item.effective_date) return item;
    const fromHistory = latestHistoryDateForId(history, fieldName, item.id);
    return fromHistory ? { ...item, effective_date: fromHistory } : item;
  });

/**
 * Сборка тела PUT /auth/{user_id} по контракту бэкенда.
 */
export const buildUserUpdatePayload = ({
  formData,
  userData,
  positions = [],
  options = {},
}) => {
  const {
    includeOrg = true,
    includeSalary = true,
    includeWorkSchedule = true,
  } = options;

  const firstPosition = positions[0];
  const isFired =
    formData.isFired !== undefined
      ? normalizeIsFired(formData.isFired)
      : normalizeIsFired(userData.is_fired);
  const payload = {
    email: (formData.email || userData.email || "").trim(),
    surname: formData.lastName || userData.surname,
    name: formData.firstName || userData.name,
    patronymic: formData.patronymic ?? userData.patronymic ?? "",
    hire_date: normalizeEffectiveDate(formData.hireDate || userData.hire_date),
    birth_date: normalizeEffectiveDate(
      formData.birthDate || userData.birth_date,
    ),
    phone_number: formData.phone ?? userData.phone_number ?? "",
    hobby: (formData.hobbies ?? userData.hobby ?? "").trim() || "нет данных",
    gender: formData.gender ?? userData.gender ?? "",
    access_levels:
      formData.accessLevel?.length > 0
        ? formData.accessLevel
        : (userData.access_levels || []).map((level) => level.id),
    access_level_effective_date: effectiveDateOrToday(
      formData.accessLevelEffectiveDate,
    ),
    is_fired: isFired,
    // Бэкенд ждёт null, если выбран статус «Не уволен»
    is_fired_date: isFired
      ? effectiveDateOrToday(formData.firedDate || userData.is_fired_date)
      : null,
    is_external_staff: Boolean(formData.isExternalStaff),
    grade: formData.grade ?? userData.hr_data?.grade ?? "",
    grade_effective_date: effectiveDateOrToday(
      formData.gradeEffectiveDate ||
        getLatestValueFromHistory(userData.user_grade_history, "grade")
          ?.effective_date,
    ),
  };

  if (includeSalary) {
    payload.salary =
      formData.salary !== "" && formData.salary != null
        ? Number(formData.salary)
        : userData.salary;
    payload.salary_effective_date = effectiveDateOrToday(
      formData.salaryEffectiveDate,
    );
  }

  if (includeWorkSchedule) {
    const workFormatDate = normalizeEffectiveDate(
      formData.workFormatEffectiveDate,
    );
    if (workFormatDate) {
      const workFormatId =
        formData.workFormat ?? userData.hr_data?.work_format?.id;
      if (workFormatId != null) {
        payload.work_format_id = workFormatId;
        payload.work_format_id_effective_date = workFormatDate;
      }
    }

    const workHoursDate = normalizeEffectiveDate(
      formData.workHoursPerDayEffectiveDate,
    );
    if (workHoursDate) {
      const workHours =
        formData.workHoursPerDay ?? userData.hr_data?.work_hours_per_day;
      if (workHours != null) {
        payload.work_hours_per_day = workHours;
        payload.work_hours_per_day_effective_date = workHoursDate;
      }
    }

    const workStartDate = normalizeEffectiveDate(
      formData.workStartTimeEffectiveDate,
    );
    if (workStartDate) {
      const workStartTime =
        formData.workStartTime || userData.hr_data?.work_start_time;
      if (workStartTime) {
        payload.work_start_time = workStartTime;
        payload.work_start_time_effective_date = workStartDate;
      }
    }
  }

  if (includeOrg) {
    const companyId = resolveCompanyId(formData, positions, userData);
    if (companyId != null) {
      const matchingCompany = (positions || []).find(
        (position) => Number(position.company?.value) === companyId,
      );
      payload.company_id = companyId;
      payload.company_id_effective_date = effectiveDateOrToday(
        matchingCompany?.companyEffectiveDate ||
          formData?.companyIdEffectiveDate ||
          latestHistoryDateForId(
            userData?.company_history,
            "company",
            companyId,
          ) ||
          userData?.company_id_effective_date ||
          userData?.company_effective_date,
      );
    }

    const companies = datedOrgItemsFromPositions(
      positions,
      (position) => position.company?.value,
      (position) => position.companyEffectiveDate,
    );
    if (companies.length > 0) {
      payload.companies = companies;
    } else if (companyId != null) {
      payload.companies = [
        {
          id: companyId,
          effective_date: payload.company_id_effective_date,
        },
      ];
    }

    // Отделение на форме не выбирается: бэкенд ставит его по отделу.
    // Дата отделения должна совпадать с датой отдела на той же карточке.
    const departmentsFromPositions = datedOrgItemsFromPositions(
      positions,
      (position) => position.department?.value,
      (position) => position.groupEffectiveDate,
    );
    const departmentItems =
      departmentsFromPositions.length > 0
        ? departmentsFromPositions
        : resolveDepartmentIds([], userData).map((id) => ({ id }));
    if (departmentItems.length > 0) {
      payload.department = withHistoryDates(
        departmentItems,
        userData?.user_department_history,
        "department",
      );
    }

    payload.group = datedOrgItemsFromPositions(
      positions,
      (position) => position.group?.value,
      (position) => position.groupEffectiveDate,
    );
    payload.role = datedOrgItemsFromPositions(
      positions,
      (position) => position.role?.value,
      (position) => position.roleEffectiveDate,
    );
    payload.section = datedOrgItemsFromPositions(
      positions,
      (position) => position.section?.value,
      (position) => position.sectionEffectiveDate,
    );
    payload.team_leaders = datedOrgItemsFromPositions(
      positions,
      (position) => position.teamLeader?.value,
      (position) => position.teamLeaderEffectiveDate,
    );

    payload.primary_group_id = firstPosition?.group?.value
      ? Number(firstPosition.group.value)
      : null;
    payload.primary_group_id_effective_date = effectiveDateOrToday(
      firstPosition?.groupEffectiveDate,
    );

    payload.primary_role_id = firstPosition?.role?.value
      ? Number(firstPosition.role.value)
      : null;
    payload.primary_role_id_effective_date = effectiveDateOrToday(
      firstPosition?.roleEffectiveDate,
    );

    payload.primary_section_id = firstPosition?.section?.value
      ? Number(firstPosition.section.value)
      : null;
    payload.primary_section_id_effective_date = effectiveDateOrToday(
      firstPosition?.sectionEffectiveDate,
    );

    payload.primary_team_leader_id = firstPosition?.teamLeader?.value
      ? Number(firstPosition.teamLeader.value)
      : null;
    payload.primary_team_leader_id_effective_date = effectiveDateOrToday(
      firstPosition?.teamLeaderEffectiveDate,
    );
  }

  return payload;
};

/** Поля, которые бэкенд принимает как явный null (не удалять при очистке) */
const NULLABLE_PAYLOAD_KEYS = new Set(["is_fired_date"]);

/** Удаляет пустые массивы и null/undefined перед отправкой */
export const cleanUserUpdatePayload = (payload) => {
  const cleaned = { ...payload };
  Object.keys(cleaned).forEach((key) => {
    if (Array.isArray(cleaned[key]) && cleaned[key].length === 0) {
      delete cleaned[key];
    } else if (
      (cleaned[key] === null || cleaned[key] === undefined) &&
      !NULLABLE_PAYLOAD_KEYS.has(key)
    ) {
      delete cleaned[key];
    }
  });
  return cleaned;
};
